import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import mascota from '../images/mascota.png';
import mascotaLogin from '../images/mascotaLogin.png';

// 'OK' o haber ido al login: no se vuelve a mostrar nunca.
const DONE_KEY = 'forallia_welcome_done';
// 'Más tarde': solo hasta que cierre la pestaña.
const LATER_KEY = 'forallia_welcome_later';
// 'Más tarde' del paso del login, independiente del anterior.
const LOGIN_LATER_KEY = 'forallia_welcome_login_later';

const GREEN = '#7dff8a';
const RED = '#ff6a4a';
const R = 34; // radio de cada hexagono de la malla
const SPEED = 0.15; // px por ms de las luces
const TRAIL_MS = 500; // duracion de la estela de cada luz

function read(storage, key) {
  try {
    return window[storage].getItem(key) === '1';
  } catch {
    return false;
  }
}

function save(storage, key) {
  try {
    window[storage].setItem(key, '1');
  } catch {}
}

// Malla hexagonal (como la de las recompensas) con luces que recorren las aristas.
// Devuelve la funcion que la para. Tambien la usa el aviso de cookies.
export function startMesh(canvas) {
  const ctx = canvas.getContext('2d');
  const H = Math.sqrt(3) * R;
  let grid, nodes, pulses, raf, last;

  const pick = (node, prev) => {
    const options = node.next.filter(n => n !== prev);
    const list = options.length ? options : node.next;
    return list[Math.random() * list.length | 0];
  };
  const spawn = () => {
    const from = nodes[Math.random() * nodes.length | 0];
    const life = 3000 + Math.random() * 4000;
    return { from, to: pick(from, null), t: 0, trail: [], life, color: Math.random() < 0.2 ? RED : GREEN };
  };

  function build() {
    const w = canvas.width = canvas.clientWidth;
    const h = canvas.height = canvas.clientHeight;
    const index = new Map();
    nodes = [];
    const node = (x, y) => {
      const key = Math.round(x) + ',' + Math.round(y);
      if (!index.has(key)) {
        index.set(key, nodes.length);
        nodes.push({ x, y, next: [] });
      }
      return nodes[index.get(key)];
    };

    grid = document.createElement('canvas');
    grid.width = w;
    grid.height = h;
    const g = grid.getContext('2d');
    const hexes = [];
    for (let col = -1; col * 1.5 * R < w + R; col++) {
      for (let row = -1; row * H < h + H; row++) {
        const cx = col * 1.5 * R;
        const cy = row * H + (col % 2 ? H / 2 : 0);
        const corners = [];
        for (let i = 0; i < 6; i++) {
          corners.push(node(cx + R * Math.cos(Math.PI / 3 * i), cy + R * Math.sin(Math.PI / 3 * i)));
        }
        corners.forEach((a, i) => {
          const b = corners[(i + 1) % 6];
          if (!a.next.includes(b)) {
            a.next.push(b);
            b.next.push(a);
          }
        });
        hexes.push({ corners, red: Math.random() < 0.12 });
      }
    }
    // Rojos al final para que sus aristas compartidas queden por encima de las verdes.
    hexes.sort((a, b) => a.red - b.red).forEach(({ corners, red }) => {
      g.beginPath();
      corners.forEach(c => g.lineTo(c.x, c.y));
      g.closePath();
      g.strokeStyle = g.shadowColor = red ? 'rgba(255, 106, 74, .45)' : 'rgba(125, 255, 138, .22)';
      g.shadowBlur = 6;
      g.stroke();
    });

    pulses = Array.from({ length: Math.max(6, Math.round(w * h / 45000)) }, spawn);
  }

  function frame(now) {
    const dt = Math.min(50, now - last);
    last = now;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(grid, 0, 0);
    ctx.lineCap = 'round';
    pulses.forEach((p, i) => {
      p.t += SPEED * dt / R;
      while (p.t >= 1) {
        p.t -= 1;
        const prev = p.from;
        p.from = p.to;
        p.to = pick(p.to, prev);
      }
      const x = p.from.x + (p.to.x - p.from.x) * p.t;
      const y = p.from.y + (p.to.y - p.from.y) * p.t;
      // La estela se mide en tiempo, no en frames, para que mida lo mismo a 60 que a 144 Hz.
      p.trail.push([x, y, now]);
      while (now - p.trail[0][2] > TRAIL_MS) p.trail.shift();
      p.life -= dt;
      if (p.life <= 0) {
        pulses[i] = spawn();
        return;
      }
      const [tailX, tailY] = p.trail[0];
      const fade = ctx.createLinearGradient(tailX, tailY, x, y);
      fade.addColorStop(0, p.color + '00');
      fade.addColorStop(1, p.color);
      ctx.globalAlpha = Math.min(1, p.life / 600);
      ctx.strokeStyle = fade;
      ctx.shadowColor = ctx.fillStyle = p.color;
      ctx.shadowBlur = 14;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      p.trail.forEach(([tx, ty]) => ctx.lineTo(tx, ty));
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalAlpha = 1;
    raf = requestAnimationFrame(frame);
  }

  build();
  window.addEventListener('resize', build);
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    ctx.drawImage(grid, 0, 0);
  } else {
    last = performance.now();
    raf = requestAnimationFrame(frame);
  }
  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', build);
  };
}

// Aviso de bienvenida para quien no esta logueado. Solo se monta con las cookies aceptadas
// (va dentro de <CookieBanner>). Dos pasos independientes:
// - 'welcome': tapa toda la web salvo el boton de usuario del menu; OK lleva al login.
// - 'login': en el login tapa todo salvo los botones de conectarse.
function WelcomeTutorial({ isAuthenticated, loading }) {
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [dismissed, setDismissed] = useState(() => read('localStorage', DONE_KEY) || read('sessionStorage', LATER_KEY));
  const [loginLater, setLoginLater] = useState(() => read('sessionStorage', LOGIN_LATER_KEY));
  const isLoginPath = /^\/(myAccount|login)/i.test(pathname);

  let step = null;
  if (!loading && !isAuthenticated) {
    if (/^\/myAccount\/?$/i.test(pathname)) step = loginLater ? null : 'login';
    else if (!dismissed && !isLoginPath && !/^\/(redes|codigos)/i.test(pathname)) step = 'welcome';
  }
  const login = step === 'login';

  // "Consigue más premios" de /codigos la vuelve a sacar aunque ya la hubiera cerrado.
  useEffect(() => {
    if (state && state.premios) setDismissed(false);
  }, [state]);

  // Si llega al login (OK, boton de perfil o cualquier enlace) ya sabe que tiene que loguearse.
  useEffect(() => {
    if (isLoginPath) {
      save('localStorage', DONE_KEY);
      setDismissed(true);
    }
  }, [isLoginPath]);

  // Paso del login: la caja va justo debajo de los botones de conectarse (que quedan por
  // encima del overlay). Se mide donde acaban en vez de adivinarlo con un padding fijo, que en
  // moviles altos o pequeños hacia que la caja subiera hasta pisarlos.
  const [loginTop, setLoginTop] = useState(null);
  useEffect(() => {
    if (step !== 'login') return undefined;
    const place = () => {
      const buttons = document.querySelector('.core');
      if (buttons) setLoginTop(Math.ceil(buttons.getBoundingClientRect().bottom) + 16);
    };
    place();
    // El logo de la cabecera carga despues y baja los botones: se vuelve a medir.
    const observer = new ResizeObserver(place);
    observer.observe(document.body);
    window.addEventListener('resize', place);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [step]);

  useEffect(() => {
    if (!step) return;
    const bodyClass = step === 'login' ? 'welcomeLogin' : 'welcomeOn';
    document.body.classList.add(bodyClass);
    const stop = startMesh(canvasRef.current);
    return () => {
      stop();
      document.body.classList.remove(bodyClass);
    };
  }, [step]);

  if (!step) return null;

  const later = () => {
    save('sessionStorage', login ? LOGIN_LATER_KEY : LATER_KEY);
    (login ? setLoginLater : setDismissed)(true);
  };

  return (
    <div className={'welcomeOverlay' + (login ? ' welcomeLoginStep' : '')}
      style={login && loginTop != null ? { paddingTop: loginTop } : undefined}>
      <canvas ref={canvasRef} className="welcomeMesh" />
      <div className="welcomeBox heroes" role="dialog" aria-modal="true">
        <img className="welcomeMascot" src={login ? mascotaLogin : mascota} alt="" />
        <p>{login
          ? '¡Conéctate en la plataforma que veas a Narux! Si no está la tuya, pronto la implementaré. Prometo que no te robaré los datos, jeje.'
          : '¡Bienvenido! Para disfrutar del universo de Forallia deberías iniciar sesión.'}</p>
        <div className="welcomeActions">
          {!login && <button className="avisoSesion-btn" onClick={() => navigate('/myAccount')}>OK</button>}
          <button className="avisoSesion-btn welcomeLater" onClick={later}>Más tarde</button>
        </div>
      </div>
    </div>
  );
}

export default WelcomeTutorial;
